import { REFERRAL_STATUS_LABELS, type ReferralStatus } from './enums';
import { REFERRAL_EDIT_KINDS, type ReferralEditKind, type TrusteeActivityEvent } from './dto/my-referrals';
import { REFERRAL_EDIT_LABELS } from './referral-edits';

export interface TrusteeActivitySource {
  referralId: string;
  createdAt: Date;
  history: readonly { id: string; fromStatus: ReferralStatus | null; toStatus: ReferralStatus; at: Date }[];
  edits: readonly { id: number | string; at: Date; changes: unknown }[];
}

const editKinds = (changes: unknown): ReferralEditKind[] =>
  Array.isArray(changes) ? REFERRAL_EDIT_KINDS.filter((k) => changes.includes(k)) : [];

export function trusteeReferralActivity(src: TrusteeActivitySource): TrusteeActivityEvent[] {
  const events: (TrusteeActivityEvent & { time: number; order: number })[] = [
    {
      id: `created:${src.referralId}`,
      kind: 'created',
      at: src.createdAt.toISOString(),
      fromStatus: null,
      toStatus: null,
      changes: [],
      time: src.createdAt.getTime(),
      order: 0,
    },
  ];
  for (const h of src.history)
    events.push({
      id: `status:${h.id}`,
      kind: h.fromStatus === 'draft' || h.fromStatus === null ? 'submitted' : 'status_changed',
      at: h.at.toISOString(),
      fromStatus: h.fromStatus,
      toStatus: h.toStatus,
      changes: [],
      time: h.at.getTime(),
      order: 2,
    });
  for (const e of src.edits) {
    const changes = editKinds(e.changes);
    if (!changes.length) continue;
    events.push({
      id: `edit:${e.id}`,
      kind: 'edited',
      at: e.at.toISOString(),
      fromStatus: null,
      toStatus: null,
      changes,
      time: e.at.getTime(),
      order: 1,
    });
  }
  return events
    .sort((a, b) => b.time - a.time || b.order - a.order)
    .map(({ time: _time, order: _order, ...event }) => event);
}

export function trusteeActivitySummary(e: TrusteeActivityEvent): string {
  switch (e.kind) {
    case 'created':
      return 'Referral started';
    case 'submitted':
      return 'Submitted to Admissions';
    case 'edited':
      return `Edited: ${e.changes.map((c) => REFERRAL_EDIT_LABELS[c]).join('; ')}`;
    case 'status_changed':
      return `Status changed: ${e.fromStatus ? REFERRAL_STATUS_LABELS[e.fromStatus] : 'None'} → ${e.toStatus ? REFERRAL_STATUS_LABELS[e.toStatus] : 'None'}`;
  }
}
